import { Alert, FormControlLabel, InputAdornment, Radio, RadioGroup, Switch } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ATMobilityBenefitOptionsDTORefundTypeEnum,
  BenefitActivationDTO,
  BenefitDTOBenefitEnum,
  BenefitTemplateItemDTO,
  CreateBenefitActivationDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';

interface ATMobilityBenefitOptions {
  limit: number;
  refundType: ATMobilityBenefitOptionsDTORefundTypeEnum;
  allowGroupTickets: boolean;
}

const Fields: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();
  const refundType = useWatch({ control, name: 'benefitOptions.refundType' });

  const { field: refundTypeField } = useController({ control, name: 'benefitOptions.refundType', rules: { required: true } });
  const { field: allowGroupTicketsField } = useController({ control, name: 'benefitOptions.allowGroupTickets' });

  return (
    <Grid container columns={12} columnSpacing={2}>
      <Grid size={12}>
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('AT_MOBILITY.fields.refundTypeChangeInfo')}
        </Alert>
      </Grid>
      <Grid size={6}>
        <RadioGroup {...refundTypeField}>
          <FormControlLabel
            value={ATMobilityBenefitOptionsDTORefundTypeEnum.Monthly}
            control={<Radio />}
            label={t('AT_MOBILITY.fields.refundTypeOptions.MONTHLY')}
          />
          <FormControlLabel
            value={ATMobilityBenefitOptionsDTORefundTypeEnum.Yearly}
            control={<Radio />}
            label={t('AT_MOBILITY.fields.refundTypeOptions.YEARLY')}
          />
        </RadioGroup>
      </Grid>
      <Grid size={6}>
        <TextFieldControl
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
          rules={{ required: true, min: 1, max: 10000 }}
          type="number"
          control={control}
          variant="outlined"
          name="benefitOptions.limit"
          label={
            refundType === ATMobilityBenefitOptionsDTORefundTypeEnum.Yearly
              ? t('AT_MOBILITY.fields.yearlyLimit')
              : t('AT_MOBILITY.fields.monthlyLimit')
          }
          margin="dense"
          fullWidth
        />
      </Grid>
      {refundType === ATMobilityBenefitOptionsDTORefundTypeEnum.Yearly && (
        <Grid size={12}>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('AT_MOBILITY.fields.yearlyRefundWarning')}
          </Alert>
        </Grid>
      )}
      <Grid size={12}>
        <FormControlLabel
          control={<Switch checked={!!allowGroupTicketsField.value} onChange={e => allowGroupTicketsField.onChange(e.target.checked)} />}
          label={t('AT_MOBILITY.fields.allowGroupTickets.label')}
          sx={{ mt: 1 }}
        />
      </Grid>
    </Grid>
  );
};

const resetATMobilityBenefitOptions = (selectedActivation?: BenefitActivationDTO | BenefitTemplateItemDTO): ATMobilityBenefitOptions => {
  const options = selectedActivation?.atMobilityOptions;
  return {
    refundType: options?.refundType ?? ATMobilityBenefitOptionsDTORefundTypeEnum.Monthly,
    limit: options?.limit ? options.limit / 100 : 0,
    allowGroupTickets: options?.allowGroupTickets ?? true,
  };
};

const createATMobilityBenefitActivation: ActivationDialogExtensions<ATMobilityBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.AtMobility,
    atMobilityOptions: {
      refundType: benefitOptions.refundType,
      limit: Math.round(benefitOptions.limit * 100),
      allowGroupTickets: benefitOptions.allowGroupTickets,
    },
  };
  await saveActivation(create);
};

const updateATMobilityBenefitActivation: ActivationDialogExtensions<ATMobilityBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    atMobilityOptions: {
      refundType: benefitOptions.refundType,
      limit: Math.round(benefitOptions.limit * 100),
      allowGroupTickets: benefitOptions.allowGroupTickets,
    },
  };
  await saveActivation(update);
};

export class ATMobilityBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<ATMobilityBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);

  onCreateActivation = createATMobilityBenefitActivation;
  onUpdateActivation = updateATMobilityBenefitActivation;
  onResetBenefitOptions? = resetATMobilityBenefitOptions;
}
